import React, { useMemo, useState } from "react";
import {
  Control,
  Controller,
  FieldValues,
  FormState,
  UseFormClearErrors,
  UseFormReset,
  UseFormSetError,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import Box from "@mui/material/Box";
import Autocomplete, {
  createFilterOptions,
} from "@material-ui/lab/Autocomplete";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormControl from "@material-ui/core/FormControl";
import TextField from "@material-ui/core/TextField";
import {
  withoutArmenianCharactersHandler,
  onlyNumericalHandler,
  onlyLatinAlphabeticalHandler,
  onlyArmenianWithCharactersHandler,
  onPasteHandlerForForeignCountries,
  onPasteHandlerForLocalCountries,
} from "../../../shared/helpers";
import HpInput from "../../../shared/ui/components/Input";
import { useDispatch, useSelector } from "react-redux";
import {
  selectCountries,
  selectMobileCodes,
} from "../../../store/slicers/template";
import {
  getDistricts,
  getStreets,
  recipientCountry,
  selectDistricts,
  selectFormData,
  selectStreets,
} from "../../../store/slicers/address";
import {
  CountryWithMobileCode,
  District,
  RecipientCountry,
  Region,
  Street,
} from "../models/address";
import { ClassNameMap } from "@material-ui/styles";
import regions from "../../../assets/constants/regions.json";
import { Typography } from "@mui/material";
import { ECountries } from "../../../resources/constants";

export const countriesList = [
  {
    id: ECountries.Armenia,
    country: "Հայաստան",
    price: 0,
    local: true,
  },
];

interface IAddressForm {
  control: Control<FieldValues, object>;
  watch: UseFormWatch<FieldValues>;
  styles: ClassNameMap;
  setValue: UseFormSetValue<FieldValues>;
  formState: FormState<any>;
  setError: UseFormSetError<any>;
  clearErrors: UseFormClearErrors<any>;
  reset: UseFormReset<any>;
}

const filter = createFilterOptions();

const AddressForm = ({
  control,
  watch,
  styles,
  setValue,
  formState,
  setError,
  clearErrors,
  reset,
}: IAddressForm): JSX.Element => {
  const dispatch = useDispatch();
  const countries = useSelector(selectCountries);
  const mobileCodes = useSelector(selectMobileCodes);
  const districts = useSelector(selectDistricts);
  const streets = useSelector(selectStreets);
  const formDataValues = useSelector(selectFormData);

  const [radioButtonValue, setRadioButtonValue] = useState<string>(
    formDataValues?.house ? "house" : "apartment"
  );

  const { errors } = formState;

  const handleCountryWithCodeChange = (
    event: React.ChangeEvent<any>,
    value: CountryWithMobileCode | string | null
  ) => {
    setValue("countryWithCodes", value);
  };

  const handleRecipientCountryChange = (
    event: React.ChangeEvent<any>,
    value: RecipientCountry | any
  ) => {
    dispatch(recipientCountry(value));
    setValue("recipientCountry", value);
    setValue("region", "");
    setValue("district", "");
    setValue("street", "");
    setValue("customRegion", "");
    setValue("customDistrict", "");
    setValue("customStreet", "");
    clearErrors([
      "region",
      "district",
      "street",
      "customRegion",
      "customDistrict",
      "customStreet",
    ]);
  };

  const handleRegionsChange = (
    event: React.ChangeEvent<any>,
    value: Region | any
  ) => {
    setValue("region", value);
    setValue("district", "");
    setValue("street", "");
    if (value) {
      dispatch(getDistricts(value.id));
    }
  };

  const handleDistrictsChange = (
    event: React.ChangeEvent<any>,
    value: District | any
  ) => {
    if (typeof value === "string") {
      setValue("district", {
        id: districts?.length + 1,
        name: value,
        region: null,
      });
    } else if (value && value.inputValue) {
      const newDistrict = {
        id: districts?.length + 1,
        name: value.inputValue,
        region: null,
      };
      // Create a new value from the user input
      setValue("district", newDistrict);
    } else {
      setValue("district", value);
    }
    setValue("street", "");
    if (value) {
      dispatch(getStreets(value.id));
    }
  };

  const handleStreetsChange = (
    event: React.ChangeEvent<any>,
    value: Street | any
  ) => {
    if (typeof value === "string") {
      setValue("street", {
        id: streets?.length + 1,
        name: value,
        district: null,
        postIndex: "",
      });
    } else if (value && value.inputValue) {
      // Create a new value from the user input
      setValue("street", {
        id: streets?.length + 1,
        name: value.inputValue,
        district: null,
        postIndex: "",
      });
    } else {
      setValue("street", value);
    }
  };

  const handleRadioButtonChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRadioButtonValue((event.target as HTMLInputElement).value);
    setValue("building", "");
    setValue("apartment", "");
    setValue("house", "");
    clearErrors(["building", "apartment", "house"]);
  };

  const validateRecipientFieldsHandler = (
    event: React.KeyboardEvent<HTMLDivElement | HTMLInputElement> | any,
    type: string
  ) => {
    const { value } = event.target;
    if (watch("recipientCountry.id") === 533) {
      const validateNotArmenian = withoutArmenianCharactersHandler(event);
      setError(`${type}`, {
        type: "manual",
        message:
          !value && !validateNotArmenian
            ? "Մուտքագրեք ռուսերեն կամ լատինատառ"
            : "",
      });
    } else {
      const validateOnlyLatin = onlyLatinAlphabeticalHandler(event);
      setError(`${type}`, {
        type: "manual",
        message:
          !value && !validateOnlyLatin ? "Մուտքագրեք միայն լատինատառ" : "",
      });
    }
  };

  const validateOnlyArmenianHandler = (
    event: React.KeyboardEvent<HTMLInputElement> | any,
    type: string
  ) => {
    const { value } = event.target;
    const validateArmenian = onlyArmenianWithCharactersHandler(event);
    setError(`${type}`, {
      type: "manual",
      message: !value && !validateArmenian ? "Մուտքագրեք միայն հայատառ" : "",
    });
  };

  const filteredCountriesList = useMemo(() => {
    return countries.filter(
      (i: any) => i.id !== 569 && i.id !== 589 && i.id !== 522 && i.id !== 586
    );
  }, [countries]);

  return (
    <>
      <p className={styles.formTitle}>
        Լրացրու տվյալներդ <span>*</span>
      </p>
      <Box mb="30px" mt="30px">
        <Controller
          control={control}
          name="countryWithCodes"
          rules={{
            required: "Պարտադիր դաշտ",
          }}
          render={({ field, fieldState }) => (
            <Autocomplete
              {...field}
              id="country-mobile-codes"
              options={mobileCodes}
              className={styles.autocomplete}
              getOptionLabel={(option: CountryWithMobileCode) =>
                option.countryName ? option.countryName : ""
              }
              value={watch("countryWithCodes") ? watch("countryWithCodes") : ""}
              onChange={handleCountryWithCodeChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Երկիր"
                  variant="outlined"
                  color="primary"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          )}
        />
      </Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb="30px"
        className={styles.box}
      >
        <div className={styles.countryCode}>
          {watch("countryWithCodes.mobileCode")}
        </div>
        <div className={styles.phoneNumber}>
          <Controller
            control={control}
            name="phoneNumber"
            rules={{
              required: "Պարտադիր դաշտ",
              minLength: {
                value: 8,
                message: "Պետք է լրացնել նվազագույնը 8 նիշ",
              },
            }}
            render={({ field: { onChange, value }, fieldState }) => (
              <HpInput
                onChange={onChange}
                onKeyPress={onlyNumericalHandler}
                onPaste={onlyNumericalHandler}
                onInput={onlyNumericalHandler}
                value={value ? value : ""}
                fullWidth
                inputProps={{ maxLength: 20 }}
                className={styles.input}
                placeholder="Հեռախոսահամար"
                error={!!fieldState.error}
              />
            )}
          />
        </div>
        {errors?.phoneNumber && (
          <p className={styles.errorMessage}>{errors.phoneNumber.message}</p>
        )}
      </Box>
      <Box mb="43px" className={styles.box}>
        <Controller
          control={control}
          name="email"
          rules={{
            required: "Պարտադիր դաշտ",
            pattern: {
              value: /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/,
              message: "Խնդրում ենք մուտքագրել վավեր էլ. հասցե",
            },
          }}
          render={({ field: { onChange, onBlur, value }, fieldState }) => (
            <HpInput
              onChange={onChange}
              onBlur={onBlur}
              value={value ? value : ""}
              fullWidth
              className={styles.input}
              placeholder="Էլ. հասցե"
              error={!!fieldState.error}
            />
          )}
        />
        {errors?.email && (
          <p className={styles.errorMessage}>{errors.email.message}</p>
        )}
      </Box>
      <p className={styles.formTitle}>Նշիր ստացողի անունը և հասցեն</p>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb="30px"
        mt="32px"
        className={styles.box}
      >
        <div className={styles.firstName}>
          <Controller
            control={control}
            name="firstName"
            rules={{
              required: "Պարտադիր դաշտ",
              validate: (value) => {
                return value && value.trim() ? true : "Պարտադիր դաշտ";
              },
            }}
            render={({ field: { onChange, onBlur, value }, fieldState }) => (
              <HpInput
                onChange={onChange}
                onBlur={onBlur}
                value={value ? value : ""}
                fullWidth
                className={styles.input}
                placeholder="Անուն"
                error={!!fieldState.error}
              />
            )}
          />
          {errors?.firstName && (
            <p className={styles.errorMessage}>{errors.firstName.message}</p>
          )}
        </div>
        <div className={styles.lastName}>
          <Controller
            control={control}
            name="lastName"
            rules={{
              required: "Պարտադիր դաշտ",
              validate: (value) => {
                return value && value.trim() ? true : "Պարտադիր դաշտ";
              },
            }}
            render={({ field: { onChange, onBlur, value }, fieldState }) => (
              <HpInput
                onChange={onChange}
                onBlur={onBlur}
                value={value ? value : ""}
                fullWidth
                className={styles.input}
                placeholder="Ազգանուն"
                error={!!fieldState.error}
              />
            )}
          />
          {errors?.lastName && (
            <p className={styles.errorMessage}>{errors.lastName.message}</p>
          )}
        </div>
      </Box>
      <Box mb="30px">
        <Controller
          control={control}
          name="recipientCountry"
          rules={{
            required: "Պարտադիր դաշտ",
          }}
          render={({ field, fieldState }) => (
            <Autocomplete
              id="countries-recipient"
              {...field}
              options={countriesList || []}
              className={styles.autocomplete}
              getOptionLabel={(option: RecipientCountry) =>
                option.country ? option.country : ""
              }
              value={watch("recipientCountry") ? watch("recipientCountry") : ""}
              onChange={handleRecipientCountryChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Երկիր"
                  variant="outlined"
                  color="primary"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          )}
        />
      </Box>
      {watch("recipientCountry.id") === ECountries.Armenia ? (
        <>
          <Box mb="30px">
            <Controller
              control={control}
              name="region"
              rules={{
                required: errors?.region?.message
                  ? errors?.region?.message
                  : "Պարտադիր դաշտ",
              }}
              render={({ field, fieldState }) => (
                <Autocomplete
                  id="region"
                  {...field}
                  options={regions}
                  disabled={!watch("recipientCountry") ? true : false}
                  className={styles.autocomplete}
                  getOptionLabel={(option: Region) =>
                    option.name ? option.name : ""
                  }
                  value={watch("region") ? watch("region") : ""}
                  onChange={handleRegionsChange}
                  onKeyPress={(event) =>
                    validateOnlyArmenianHandler(event, "region")
                  }
                  onInput={(event) =>
                    validateOnlyArmenianHandler(event, "region")
                  }
                  onPaste={(event) =>
                    validateOnlyArmenianHandler(event, "region")
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Մարզ"
                      variant="outlined"
                      color="primary"
                      error={
                        fieldState.isTouched && errors?.region ? true : false
                      }
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              )}
            />
          </Box>
          <Box mb="30px">
            <Controller
              control={control}
              name="district"
              rules={{
                required: errors?.district?.message
                  ? errors?.district?.message
                  : "Պարտադիր դաշտ",
              }}
              render={({ field, fieldState }) => (
                <Autocomplete
                  id="district"
                  {...field}
                  options={districts}
                  freeSolo
                  filterOptions={(options: any, params: any) => {
                    const filtered = filter(options, params);
                    const { inputValue } = params;
                    // Suggest the creation of a new value
                    const isExisting = options.some(
                      (option: any) => inputValue === option.name
                    );
                    if (inputValue !== "" && !isExisting) {
                      filtered.push({
                        id: districts.length + 1,
                        name: inputValue,
                        region: null,
                      });
                    }
                    return filtered;
                  }}
                  renderOption={(option) => option.name}
                  disabled={!watch("region") ? true : false}
                  className={styles.autocomplete}
                  getOptionLabel={(option) => {
                    // Value selected with enter, right from the input
                    if (typeof option === "string") {
                      return option;
                    }
                    // Add "xxx" option created dynamically
                    if (option.inputValue) {
                      return option.inputValue;
                    }
                    // Regular option
                    return option.name;
                  }}
                  value={watch("district") ? watch("district") : ""}
                  onChange={handleDistrictsChange}
                  onKeyPress={(event) =>
                    validateOnlyArmenianHandler(event, "district")
                  }
                  onInput={(event) =>
                    validateOnlyArmenianHandler(event, "district")
                  }
                  onPaste={(event) =>
                    validateOnlyArmenianHandler(event, "district")
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Քաղաք/թաղամաս"
                      variant="outlined"
                      color="primary"
                      error={
                        fieldState.isTouched && errors?.district ? true : false
                      }
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              )}
            />
          </Box>
          <Box mb="44px">
            <Controller
              control={control}
              name="street"
              rules={{
                required: errors?.street?.message
                  ? errors?.street?.message
                  : "Պարտադիր դաշտ",
              }}
              render={({ field, fieldState }) => (
                <Autocomplete
                  id="str_eet"
                  {...field}
                  options={streets}
                  freeSolo
                  filterOptions={(options: any, params: any) => {
                    const filtered = filter(options, params);
                    const { inputValue } = params;
                    // Suggest the creation of a new value
                    const isExisting = options.some(
                      (option: any) => inputValue === option.name
                    );
                    if (inputValue !== "" && !isExisting) {
                      const newValue = {
                        id: streets.length + 1,
                        name: inputValue,
                        postIndex: "",
                        district: null,
                      };
                      filtered.push(newValue);
                    }
                    return filtered;
                  }}
                  renderOption={(option) => option.name}
                  disabled={!watch("district") ? true : false}
                  className={styles.autocomplete}
                  getOptionLabel={(option) => {
                    // Value selected with enter, right from the input
                    if (typeof option === "string") {
                      return option;
                    }
                    // Add "xxx" option created dynamically
                    if (option.inputValue) {
                      return option.inputValue;
                    }
                    // Regular option
                    return option.name;
                  }}
                  value={watch("street") ? watch("street") : ""}
                  onChange={handleStreetsChange}
                  onKeyPress={(event) =>
                    validateOnlyArmenianHandler(event, "street")
                  }
                  onInput={(event) =>
                    validateOnlyArmenianHandler(event, "street")
                  }
                  onPaste={(event) =>
                    validateOnlyArmenianHandler(event, "street")
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Փողոց"
                      variant="outlined"
                      color="primary"
                      error={
                        fieldState.isTouched && errors?.street ? true : false
                      }
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              )}
            />
          </Box>
        </>
      ) : (
        <>
          <Box mb="30px" className={styles.box}>
            <Typography color="grey" fontSize={12}>
              * Լրացրեք միայն լատինատառ
            </Typography>
            <Controller
              control={control}
              name="customRegion"
              rules={{
                pattern: {
                  value: /^[A-Za-z0-9]+$/,
                  message: "error",
                },
                required: errors?.customRegion?.message
                  ? errors?.customRegion?.message
                  : "Պարտադիր դաշտ",
                validate: (value) => {
                  //edited
                  return watch("recipientCountry.id") !== ECountries.Armenia &&
                    value &&
                    value.trim()
                    ? true
                    : "Պարտադիր դաշտ";
                },
              }}
              render={({ field: { onChange, onBlur, value }, fieldState }) => (
                <HpInput
                  onChange={onChange}
                  onBlur={onBlur}
                  onKeyPress={
                    watch("recipientCountry.id") !== 586
                      ? (event: any) =>
                          validateRecipientFieldsHandler(event, "customRegion")
                      : (event: any) =>
                          validateOnlyArmenianHandler(event, "customRegion")
                  }
                  onPaste={
                    watch("recipientCountry.id") !== 586
                      ? (event: any) =>
                          onPasteHandlerForForeignCountries(
                            event,
                            "customRegion",
                            watch,
                            setError
                          )
                      : (event: any) =>
                          onPasteHandlerForLocalCountries(
                            event,
                            "customRegion",
                            setError
                          )
                  }
                  onInput={
                    watch("recipientCountry.id") !== 586
                      ? (event: any) =>
                          validateRecipientFieldsHandler(event, "customRegion")
                      : (event: any) =>
                          validateOnlyArmenianHandler(event, "customRegion")
                  }
                  value={value ? value : ""}
                  fullWidth
                  className={styles.input}
                  placeholder="Մարզ"
                  error={
                    //edited
                    fieldState.isTouched &&
                    watch("recipientCountry.id") !== ECountries.Armenia &&
                    errors?.customRegion
                      ? true
                      : false
                  }
                />
              )}
            />
            {errors?.customRegion && (
              <p className={styles.errorMessage}>
                {errors.customRegion.message}
              </p>
            )}
          </Box>
          <Box mb="30px" className={styles.box}>
            <Typography color="grey" fontSize={12}>
              * Լրացրեք միայն լատինատառ
            </Typography>
            <Controller
              control={control}
              name="customDistrict"
              rules={{
                pattern: {
                  value: /^[A-Za-z0-9]+$/,
                  message: "error",
                },
                required: errors?.customDistrict?.message
                  ? errors?.customDistrict?.message
                  : "Պարտադիր դաշտ",
                validate: (value) => {
                  //edited
                  return watch("recipientCountry.id") !== ECountries.Armenia &&
                    value &&
                    value.trim()
                    ? true
                    : "Պարտադիր դաշտ";
                },
              }}
              render={({ field: { onChange, onBlur, value }, fieldState }) => (
                <HpInput
                  onChange={onChange}
                  onBlur={onBlur}
                  onKeyPress={
                    watch("recipientCountry.id") !== 586
                      ? (event: any) =>
                          validateRecipientFieldsHandler(
                            event,
                            "customDistrict"
                          )
                      : (event: any) =>
                          validateOnlyArmenianHandler(event, "customDistrict")
                  }
                  onPaste={
                    watch("recipientCountry.id") !== 586
                      ? (event: any) =>
                          onPasteHandlerForForeignCountries(
                            event,
                            "customDistrict",
                            watch,
                            setError
                          )
                      : (event: any) =>
                          onPasteHandlerForLocalCountries(
                            event,
                            "customDistrict",
                            setError
                          )
                  }
                  onInput={
                    watch("recipientCountry.id") !== 586
                      ? (event: any) =>
                          validateRecipientFieldsHandler(
                            event,
                            "customDistrict"
                          )
                      : (event: any) =>
                          validateOnlyArmenianHandler(event, "customDistrict")
                  }
                  value={value ? value : ""}
                  fullWidth
                  className={styles.input}
                  placeholder="Քաղաք/թաղամաս"
                  error={
                    //edited
                    fieldState.isTouched &&
                    watch("recipientCountry.id") !== ECountries.Armenia &&
                    errors?.customDistrict
                      ? true
                      : false
                  }
                />
              )}
            />
            {errors?.customDistrict && (
              <p className={styles.errorMessage}>
                {errors.customDistrict.message}
              </p>
            )}
          </Box>
          <Box mb="44px" className={styles.box}>
            <Typography color="grey" fontSize={12}>
              * Լրացրեք միայն լատինատառ
            </Typography>
            <Controller
              control={control}
              name="customStreet"
              rules={{
                pattern: {
                  value: /^[A-Za-z0-9]+$/,
                  message: "error",
                },
                required: errors?.customStreet?.message
                  ? errors?.customStreet?.message
                  : "Պարտադիր դաշտ",
                validate: (value) => {
                  //edited
                  return watch("recipientCountry.id") !== ECountries.Armenia &&
                    value &&
                    value.trim()
                    ? true
                    : "Պարտադիր դաշտ";
                },
              }}
              render={({ field: { onChange, onBlur, value }, fieldState }) => (
                <HpInput
                  onChange={onChange}
                  onBlur={onBlur}
                  onKeyPress={
                    watch("recipientCountry.id") !== 586
                      ? (event: any) =>
                          validateRecipientFieldsHandler(event, "customStreet")
                      : (event: any) =>
                          validateOnlyArmenianHandler(event, "customStreet")
                  }
                  onPaste={
                    watch("recipientCountry.id") !== 586
                      ? (event: any) =>
                          onPasteHandlerForForeignCountries(
                            event,
                            "customStreet",
                            watch,
                            setError
                          )
                      : (event: any) =>
                          onPasteHandlerForLocalCountries(
                            event,
                            "customStreet",
                            setError
                          )
                  }
                  onInput={
                    watch("recipientCountry.id") !== 586
                      ? (event: any) =>
                          validateRecipientFieldsHandler(event, "customStreet")
                      : (event: any) =>
                          validateOnlyArmenianHandler(event, "customStreet")
                  }
                  value={value ? value : ""}
                  fullWidth
                  className={styles.input}
                  placeholder="Փողոց"
                  error={
                    //edited
                    fieldState.isTouched &&
                    watch("recipientCountry.id") !== ECountries.Armenia &&
                    errors?.customStreet
                      ? true
                      : false
                  }
                />
              )}
            />
            {errors?.customStreet && (
              <p className={styles.errorMessage}>
                {errors.customStreet.message}
              </p>
            )}
          </Box>
        </>
      )}
      <FormControl component="fieldset" fullWidth>
        <RadioGroup
          aria-label="Building"
          name="building"
          value={radioButtonValue}
          onChange={handleRadioButtonChange}
        >
          <FormControlLabel
            value="apartment"
            control={<Radio color="primary" />}
            label="Բնակարան"
          />
          <Box mt="30px" className={styles.box}>
            <Controller
              control={control}
              name="building"
              rules={{
                required: "Պարտադիր դաշտ",
                validate: (value) => {
                  return value && value.trim() ? true : "Պարտադիր դաշտ";
                },
              }}
              render={({ field: { onChange, onBlur, value }, fieldState }) => (
                <HpInput
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value ? value : ""}
                  disabled={radioButtonValue === "house"}
                  fullWidth
                  className={styles.input}
                  placeholder="Շենք"
                  error={radioButtonValue === "apartment" && !!fieldState.error}
                />
              )}
            />
            {radioButtonValue === "apartment" && errors?.building && (
              <p className={styles.errorMessage}>{errors.building.message}</p>
            )}
          </Box>
          <Box mt="30px" mb="44px" className={styles.box}>
            <Controller
              control={control}
              name="apartment"
              rules={{
                required: "Պարտադիր դաշտ",
                validate: (value) => {
                  return value && value.trim() ? true : "Պարտադիր դաշտ";
                },
              }}
              render={({ field: { onChange, onBlur, value }, fieldState }) => (
                <HpInput
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value ? value : ""}
                  fullWidth
                  className={styles.input}
                  disabled={radioButtonValue === "house"}
                  placeholder="Բնակարան"
                  error={radioButtonValue === "apartment" && !!fieldState.error}
                />
              )}
            />
            {radioButtonValue === "apartment" && errors?.apartment && (
              <p className={styles.errorMessage}>{errors.apartment.message}</p>
            )}
          </Box>
          <FormControlLabel
            value="house"
            control={<Radio color="primary" />}
            label="Տուն"
          />
          <Box mt="30px" mb="30px" className={styles.box}>
            <Controller
              control={control}
              name="house"
              rules={{
                required: "Պարտադիր դաշտ",
                validate: (value) => {
                  return value && value.trim() ? true : "Պարտադիր դաշտ";
                },
              }}
              render={({ field: { onChange, onBlur, value }, fieldState }) => (
                <HpInput
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value ? value : ""}
                  fullWidth
                  className={`${styles.input}`}
                  placeholder="Տուն"
                  disabled={radioButtonValue === "apartment"}
                  error={radioButtonValue === "house" && !!fieldState.error}
                />
              )}
            />
            {radioButtonValue === "house" && errors?.house && (
              <p className={styles.errorMessage}>{errors.house.message}</p>
            )}
          </Box>
        </RadioGroup>
      </FormControl>
    </>
  );
};

export default AddressForm;
